import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCurrentRoute } from 'react-navi';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { findIndex } from 'ramda';
import {
  AuthContext,
  clearBonusCode,
  Payment,
  setBonusCode,
  toCamel,
  useApi,
  uuid,
} from '@repo-level/core-front';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';

import { Button } from 'components/buttons';
import { useTheme } from 'hooks/theme';
import { BonusGroupItem, BonusI, BonusItemGeneral } from 'api/cms/bonuses';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { Slider } from 'components/Slider';
import { Checkbox } from 'components/form/Checkbox';
import { getTimeLeftFromScheduler } from 'helpers/timer';
import { TimerLabel } from 'components/Timer';
import { CMSGeneralButtonType, CMSImageBase } from 'api/cms/base';
import { useHandleBonus } from 'hooks/bonuses';
import { useFranceLocale } from 'hooks/franceLocale';

import {
  ScActiveBonusDetails,
  ScBonus,
  ScBonusesGroup,
  ScBonusCard,
  ScMoreBtn,
  ScTermsAndConditions,
  ScBonusCardSmall,
  ScTermsAndConditionsBlock,
  ScShowMoreBtnProps,
} from './styled';
import { CRYPTO_METHOD } from 'components/Deposit/PaymentMethod/helpers';
import { FormGroup } from 'components/form/groups/FormGroup';
import { CheckboxBlock } from 'components/form/primitives/CheckboxBlock';
import { getCurrencyName, mapCurrencySymbol } from 'helpers/currency';

interface BonusCardProps {
  title: string;
  offer: string;

  background: CMSImageBase | null;
  character: CMSImageBase | null;

  activeBonus?: BonusItemGeneral;
  bonuses?: BonusGroupItem[];
}

const checkHasTermsAndConditions = (
  termsAndConditions: string | null | undefined
) => {
  if (!termsAndConditions) return false;

  const tagRegex = /<[^>]*>/gm;
  const contentRegex = /[^<>\s]+/gm;

  return contentRegex.test(termsAndConditions?.replace(tagRegex, ''));
};

export const BONUS_REF_PARAM = 'bonus-ref';

export const BonusCard: FC<BonusCardProps> = ({
  title,
  offer,
  background,
  character,
  bonuses,
  activeBonus,
}) => {
  const { t } = useTranslation('glossary');

  const {
    url: { query },
  } = useCurrentRoute();

  const {
    minDepIcon: MinDepIcon,
    wagerIcon: WagerIcon,
    frequencyIcon: FrequencyIcon,
    maxBonusAmountIcon: MaxBonusAmountIcon,
    additionalInfoIcon: AdditionalInfoIcon,
    depNumberIcon: DepNumberIcon,
    fsAmountIcon: FsAmountIcon,
    gameIcon: GameIcon,
    maxWinFsIcon: MaxWinFsIcon,
    wagerTimeIcon: WagerTimeIcon,
  } = useTheme();

  const { isMobileS } = useContext(CommonUtilsContext);
  const { isAuthenticated } = useContext(AuthContext);

  const { isFRWithoutStag } = useFranceLocale();

  const [isTermsAndConditionsOpen, toggleTermsAndConditionsOpen] = useState(
    false
  );

  const ref = useRef<HTMLDivElement>(null);

  const [isBonusSelected, setIsBonusSelected] = useState<boolean>(false);

  const {
    description,
    frequency,
    wager,
    minDep,
    maxBonusAmount,
    additionalInfo,
    depNumber,
    fsAmount,
    game,
    maxWinFs,
    wagerTime,
    termsAndConditions,
    code,
    id: activeBonusId,
    buttonName,
    buttonLink,
    buttonType,
    scheduler,
    countdownToggle,
    identifier,
  } = activeBonus || {};

  const { handleBonus, pendingBonus } = useHandleBonus(code);

  const timer = useMemo(
    () => (countdownToggle ? getTimeLeftFromScheduler(scheduler) : null),
    [scheduler, countdownToggle]
  );

  const hasTermsAndConditions = useMemo(
    () => checkHasTermsAndConditions(termsAndConditions),
    [termsAndConditions]
  );

  const bonusButtonParams = useMemo(() => {
    const getText = (trans: string) => {
      if (buttonName == null || buttonName === '')
        return t(`${toCamel(trans)}`);

      return buttonName;
    };

    const getLink = (() => {
      if (buttonLink == null || buttonLink === '') return undefined;
      return buttonLink;
    })();

    switch (buttonType) {
      case CMSGeneralButtonType.DEPOSIT:
        return {
          text: `${
            isAuthenticated
              ? getText('useBonus')
              : isFRWithoutStag
              ? t('login')
              : t('register')
          }`,
          click: handleBonus,
        };
      case CMSGeneralButtonType.SHOW_MORE:
        return {
          text: getText('details'),
          link: getLink,
        };
      case CMSGeneralButtonType.EXPLORE:
        return {
          text: getText(CMSGeneralButtonType.EXPLORE),
          link: getLink,
        };
      case CMSGeneralButtonType.READ_MORE:
        return {
          text: getText(CMSGeneralButtonType.READ_MORE),
          link: getLink,
        };
      case CMSGeneralButtonType.JOIN_NOW:
        return {
          text: getText(CMSGeneralButtonType.JOIN_NOW),
          link: getLink,
        };
      case CMSGeneralButtonType.PLAY_NOW:
        return {
          text: getText(CMSGeneralButtonType.PLAY_NOW),
          link: getLink,
        };
      default:
        return {
          text: getText('useBonus'),
          click: handleBonus,
        };
    }
  }, [
    buttonType,
    buttonName,
    t,
    buttonLink,
    isAuthenticated,
    isFRWithoutStag,
    handleBonus,
  ]);

  useEffect(() => {
    if (query[BONUS_REF_PARAM] && query[BONUS_REF_PARAM] === identifier) {
      setIsBonusSelected(true);
    } else {
      setIsBonusSelected(false);
    }
  }, [query, identifier, isAuthenticated]);

  useEffect(() => {
    if (isBonusSelected) {
      window.scrollTo({
        behavior: 'smooth',
        top: (ref?.current?.getBoundingClientRect().top || 0) - 64,
      });
    }
  }, [isBonusSelected, isAuthenticated]);

  return (
    <ScBonusCard
      banner={background?.url}
      isGroup={!!bonuses}
      titleLength={title.length}
      offerLength={offer.length}
      ref={ref}
    >
      <a onClick={bonusButtonParams.click} href={bonusButtonParams.link}>
        <div className="banner">
          <div className="content">
            <header>
              <div className="title">{title}</div>
              <div className="offer">{offer}</div>
            </header>

            {bonuses && (
              <BonusesGroup bonuses={bonuses} activeBonusId={activeBonusId} />
            )}

            <Button wide={!isMobileS} blue xLarge pending={pendingBonus}>
              {bonusButtonParams.text}
            </Button>
          </div>
          <div className="image-container">
            <img src={character?.url} alt="character" />
          </div>
        </div>
      </a>

      <ScActiveBonusDetails withPaddingBottom={!hasTermsAndConditions}>
        <div className="inner-container">
          {description && (
            <div
              className="description"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          )}
          <div className="details">
            {!!timer && (
              <div className="details__item timer">
                <FrequencyIcon />
                <div className="details__item__value">
                  {t('expiringIn')} <TimerLabel timeLeft={timer} />
                </div>
              </div>
            )}
            {frequency && (
              <div className="details__item">
                <FrequencyIcon />
                <span className="details__item__value">{frequency}</span>
              </div>
            )}
            {wager && (
              <div className="details__item">
                <WagerIcon />
                <span className="details__item__value">{wager}</span>
              </div>
            )}
            {minDep && (
              <div className="details__item">
                <MinDepIcon />
                <span className="details__item__value">{minDep}</span>
              </div>
            )}
            {maxBonusAmount && (
              <div className="details__item">
                <MaxBonusAmountIcon />
                <span className="details__item__value">{maxBonusAmount}</span>
              </div>
            )}
            {additionalInfo && (
              <div className="details__item">
                <AdditionalInfoIcon />
                <span className="details__item__value">{additionalInfo}</span>
              </div>
            )}
            {depNumber && (
              <div className="details__item">
                <DepNumberIcon />
                <span className="details__item__value">{depNumber}</span>
              </div>
            )}
            {fsAmount && (
              <div className="details__item">
                <FsAmountIcon />
                <span className="details__item__value">{fsAmount}</span>
              </div>
            )}
            {game && (
              <div className="details__item">
                <GameIcon />
                <span className="details__item__value">{game}</span>
              </div>
            )}
            {maxWinFs && (
              <div className="details__item">
                <MaxWinFsIcon />
                <span className="details__item__value">{maxWinFs}</span>
              </div>
            )}
            {wagerTime && (
              <div className="details__item">
                <WagerTimeIcon />
                <span className="details__item__value">{wagerTime}</span>
              </div>
            )}

            {hasTermsAndConditions && (
              <ShowMoreBtn
                isOpen={isTermsAndConditionsOpen}
                onClick={() => toggleTermsAndConditionsOpen((s) => !s)}
                isListItem
              />
            )}
          </div>

          {termsAndConditions && hasTermsAndConditions && (
            <AnimatePresence>
              <ScTermsAndConditions
                isOpen={isTermsAndConditionsOpen}
                as={motion.div}
                transition={{ duration: 0.3 }}
                animate={isTermsAndConditionsOpen ? 'open' : 'closed'}
                initial={{ height: isMobileS ? '72px' : '48px' }}
                variants={{
                  closed: {
                    height: isMobileS ? '72px' : '48px',
                  },
                  open: { height: 'auto' },
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: termsAndConditions,
                  }}
                />
              </ScTermsAndConditions>
            </AnimatePresence>
          )}
        </div>
      </ScActiveBonusDetails>
    </ScBonusCard>
  );
};

export const BonusCardSmall: FC<
  Omit<BonusCardProps, 'activeBonus' | 'bonuses'> &
    Pick<
      BonusI,
      | 'termsAndConditions'
      | 'code'
      | 'scheduler'
      | 'countdownToggle'
      | 'minDepV2'
    > & {
      paymentMethod?: Payment.Method;
    }
> = ({
  offer,
  title,
  background,
  character,
  termsAndConditions,
  code,
  scheduler,
  countdownToggle,
  minDepV2: minDeposit,
  paymentMethod,
}) => {
  const { t } = useTranslation('deposits');
  const { frequencyIcon: FrequencyIcon } = useTheme();

  const { user, fetchUser, pendingUser, currentAmount } = useContext(
    AuthContext
  );

  const [requestsSetBonusCode, { pending: pendingSetBonusCode }] = useApi(
    setBonusCode
  );
  const [requestClearBonusCode, { pending: pendingClearBonusCode }] = useApi(
    clearBonusCode
  );

  const timer = useMemo(
    () => (countdownToggle ? getTimeLeftFromScheduler(scheduler) : null),
    [scheduler, countdownToggle]
  );

  const [isTermsAndConditionsOpen, toggleTermsAndConditionsOpen] = useState(
    false
  );

  const hasTermsAndConditions = useMemo(
    () => checkHasTermsAndConditions(termsAndConditions),
    [termsAndConditions]
  );

  const userMinDeposit = useMemo(() => {
    return minDeposit?.find((item) => item.currency === user?.currency);
  }, [minDeposit, user?.currency]);

  const isCryptoMethod = paymentMethod?.type.includes(CRYPTO_METHOD);

  const showMinDepError =
    !isCryptoMethod &&
    !!userMinDeposit &&
    currentAmount &&
    Number(currentAmount) < Number(userMinDeposit.amount);

  const isBonusCodeActive = user?.depositBonusCode === code;

  const handleBonus = useCallback(async () => {
    if (
      pendingClearBonusCode ||
      pendingSetBonusCode ||
      pendingUser ||
      showMinDepError
    ) {
      return;
    }

    const currentCode = user?.depositBonusCode;

    if (currentCode === code) {
      const { error } = await requestClearBonusCode().request;
      if (!error) {
        fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
      }
      return;
    }

    if (currentCode && currentCode !== code) {
      const { error: clearError } = await requestClearBonusCode().request;
      if (clearError) return;
    }

    if (code) {
      const { error: setError } = await requestsSetBonusCode({
        depositBonusCode: code,
      }).request;
      if (!setError) {
        fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
      }
    }
  }, [
    pendingClearBonusCode,
    pendingSetBonusCode,
    pendingUser,
    showMinDepError,
    user?.depositBonusCode,
    code,
    requestClearBonusCode,
    requestsSetBonusCode,
    fetchUser,
  ]);

  useEffect(() => {
    if (showMinDepError && user?.depositBonusCode === code) {
      requestClearBonusCode().request.then(({ error }) => {
        if (!error) {
          fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
        }
      });
    }
  }, [
    showMinDepError,
    user?.depositBonusCode,
    code,
    requestClearBonusCode,
    fetchUser,
  ]);

  return (
    <ScBonusCardSmall
      banner={background?.url}
      onClick={handleBonus}
      withTimer={!!timer}
      isSelected={user?.depositBonusCode === code}
      minDepositError={!!showMinDepError}
    >
      <div className="banner">
        <div className="image-container">
          <img src={character?.url} alt="character" />
        </div>

        <div className="content">
          {!!timer && (
            <div className="timer-label">
              <FrequencyIcon />
              <div className="timer">
                <TimerLabel timeLeft={timer} />
              </div>
            </div>
          )}
          <div className="title">{title}</div>
          <div className="offer">{offer}</div>

          {hasTermsAndConditions && (
            <ShowMoreBtn
              isOpen={isTermsAndConditionsOpen}
              onClick={(e) => {
                e.stopPropagation();
                toggleTermsAndConditionsOpen((s) => !s);
              }}
            />
          )}
        </div>

        <FormGroup>
          <CheckboxBlock
            id={uuid()}
            checked={isBonusCodeActive}
            onChange={handleBonus}
            disabled={
              pendingClearBonusCode || pendingSetBonusCode || pendingUser
            }
            width={24}
            height={24}
          />
        </FormGroup>
      </div>
      {termsAndConditions && hasTermsAndConditions && (
        <AnimatePresence>
          <ScTermsAndConditionsBlock
            as={motion.div}
            layout
            transition={{ duration: 0.3 }}
            initial={{ height: 0, opacity: 0 }}
            animate={isTermsAndConditionsOpen ? 'open' : 'closed'}
            variants={{
              closed: {
                height: 0,
                opacity: 0,
              },
              open: { height: 'auto', opacity: 1 },
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: termsAndConditions,
              }}
            />
          </ScTermsAndConditionsBlock>
        </AnimatePresence>
      )}
      {showMinDepError && userMinDeposit && !isCryptoMethod ? (
        <span className="min-dep">
          {getCurrencyName(mapCurrencySymbol[userMinDeposit?.currency].symbol)}
          {userMinDeposit.amount} {t('minDepError')}
        </span>
      ) : null}
    </ScBonusCardSmall>
  );
};

interface ShowMoreBtnProps extends ScShowMoreBtnProps {
  onClick: (args: any) => void;
}

export const ShowMoreBtn: FC<ShowMoreBtnProps> = ({
  isOpen,
  onClick,
  isListItem,
}) => {
  const { t } = useTranslation('glossary');

  const { images } = useTheme();

  return (
    <ScMoreBtn isListItem={isListItem} isOpen={isOpen} onClick={onClick}>
      <span>
        {isOpen ? t('less') : t('more')}
        <images.arrowRightSmallIcon.component />
      </span>
    </ScMoreBtn>
  );
};

interface BonusesGroupProps {
  bonuses: BonusGroupItem[];
  activeBonusId?: number;
}

const BonusesGroup: FC<BonusesGroupProps> = ({ bonuses, activeBonusId }) => {
  const { numbers } = useTheme();

  const { isMobileS } = useContext(CommonUtilsContext);

  const bonusesData = useMemo(
    () =>
      bonuses.map((bonus, index) => {
        const NumberIcon =
          numbers[
            `number-${activeBonusId === bonus.id ? 'shine-' : ''}${index + 1}`
          ].component;

        return {
          ...bonus,
          icon: <NumberIcon />,
        };
      }),
    [bonuses, activeBonusId, numbers]
  );

  const activeBonusIndex = useMemo(
    () => findIndex(({ id }) => id === activeBonusId, bonusesData),
    [bonusesData, activeBonusId]
  );

  return (
    <ScBonusesGroup>
      {isMobileS ? (
        <Slider
          slides={bonusesData}
          slide={({ id, offer, icon, isActive }, idx) => (
            <ScBonus
              isUsed={!!isActive && idx < activeBonusIndex}
              isActive={activeBonusId === id}
              key={id}
            >
              {icon}
              <span>{offer}</span>
            </ScBonus>
          )}
          slidesPerView={1.8}
          spaceBetween={8}
          initialSlide={activeBonusIndex}
          speed={800}
        />
      ) : (
        bonusesData.map(({ id, offer, icon, isActive }, idx) => (
          <ScBonus
            isUsed={!!isActive && idx < activeBonusIndex}
            isActive={activeBonusId === id}
            key={id}
          >
            {icon}
            {offer}
          </ScBonus>
        ))
      )}
    </ScBonusesGroup>
  );
};
